import React from 'react';
import TextSearch from '../../controls/TextSearch';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import RoleForm from './RoleForm';
import RoleGroupsForm from './RoleGroupsForm';
import { UserRole } from '../../../data/models/user-role.model';
import { UserGroup } from '../../../data/models/user-group.model';
import * as uiSlice from '../../../data/ui.slice';
import * as securitySlice from '../../../data/security.slice';
import securityUtils from '../../../services/securityUtils';
import ConfirmationDialog from '../../ConfirmationDialog';
import { ApplicationTitle } from '../../../data/models/application-title.model';
import { UserCategoryType } from '../../../data/models/user-category-type.model';
import SearchIcon from '@mui/icons-material/Search';

const PREFIX = 'RoleDialog';

const classes = {
  dialog: `${PREFIX}-dialog`,
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  basicInfo: `${PREFIX}-basicInfo`,
  scroll: `${PREFIX}-scroll`,
  groups: `${PREFIX}-groups`,
  actions: `${PREFIX}-actions`,
  container: `${PREFIX}-container`,
  noRows: `${PREFIX}-noRows`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialog}`]: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
  },
  [`& .${classes.basicInfo}`]: {
    minWidth: 400,
  },
  [`& .${classes.scroll}`]: {
    overflowY: 'auto',
    flex: 1,
  },
  [`& .${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [`& .${classes.groups}`]: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 400,
  },
  [`& .${classes.actions}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
    justifyContent: 'start',
    padding: theme.spacing(2),
  },
  [`& .${classes.noRows}`]: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}));

interface RoleDialogProps {
  open: boolean;
  role?: UserRole;
  applications: ApplicationTitle[];
  categoryTypes: UserCategoryType[];
  groups: UserGroup[];
  close: (saved: boolean) => void;
}

export default function RoleDialog({
  open,
  role,
  applications,
  categoryTypes,
  groups,
  close,
}: RoleDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');
  const title = role ? t('roles.editRole') : t('roles.createRole');

  const saving = useAppSelector(securitySlice.selectSavingUserRole);
  const saved = useAppSelector(securitySlice.selectSavedUserRole);
  const savedMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [hasGroupSelection, setHasGroupSelection] = React.useState(false);
  const [filteredGroups, setFilteredGroups] = React.useState<UserGroup[]>([]);
  const [groupSearch, setGroupSearch] = React.useState('');

  const { control, formState, handleSubmit, reset, watch } = useForm({
    mode: 'onTouched',
  });
  const { isDirty, isValid } = formState;

  const groupSelections = watch();
  const applicationId = watch('ApplicationID');
  const userCategoryId = watch('CategoryTypeID');

  React.useEffect(() => {
    const keys = Object.keys(groupSelections);
    let hasSelection = keys.some((key) => {
      return key.startsWith('GroupID_') && groupSelections[key];
    });
    setHasGroupSelection(hasSelection);
  }, [groupSelections]);

  React.useEffect(() => {
    if (open) {
      const defaults: any = {
        ...securityUtils.getRoleDefaults(role, groups),
      };
      reset(defaults);
    }
  }, [open, role, groups]);

  React.useEffect(() => {
    if (groups && applicationId) {
      setFilteredGroups(
        groups.filter((g) => {
          const search = groupSearch.toLowerCase().trim();
          return (
            g.userGroupState === 'active' &&
            g.applicationTitleID === applicationId &&
            g.userCategoryTypeID === userCategoryId &&
            g.userGroupName.toLowerCase().includes(search)
          );
        }),
      );
    }
  }, [groups, applicationId, userCategoryId, groupSearch]);

  React.useEffect(() => {
    if (saved && savedMessage) {
      close(true);
    }
  }, [saved, savedMessage]);

  const saveRole = React.useCallback(
    (data: any) => {
      dispatch(securitySlice.setSavingUserRole(false));
      dispatch(securitySlice.setSavedUserRole(false));
      const newRole = securityUtils.getRoleFromFormData(
        role ? role.userRoleID : undefined,
        data,
        groups,
      );
      if (role) {
        dispatch(securitySlice.updateUserRoleAsync(newRole));
      } else {
        dispatch(securitySlice.createUserRoleAsync(newRole));
      }
    },
    [dispatch, role, groups],
  );

  const cancelDialog = () => {
    close(false);
    handleConfirmationDialogClose();
  };

  const handleConfirmationDialogOpen = () => {
    setConfirmOpen(true);
  };
  const handleConfirmationDialogClose = () => {
    setConfirmOpen(false);
  };

  const isFormDirty = () => {
    if (isDirty) {
      handleConfirmationDialogOpen();
    } else {
      cancelDialog();
    }
  };

  return (
    <RootDialog
      className={classes.content}
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={isFormDirty}>
      <DialogTitle className={classes.title}>{title}</DialogTitle>
      <DialogContent className={classes.content}>
        <Card>
          <CardHeader title={t('roles.basicInfo')} />
          <CardContent className={classes.basicInfo}>
            <RoleForm
              applications={applications}
              categoryTypes={categoryTypes}
              control={control}
              formState={formState}
              requiredFieldMessage={requiredFieldMessage}
              isNewRole={!role}
              readOnly={false}
            />
          </CardContent>
        </Card>
        <Card className={classes.scroll}>
          <CardHeader title={t('roles.groups')} />
          <CardContent className={classes.groups}>
            <Box className={classes.container}>
              <TextSearch
                icon={<SearchIcon />}
                id="group-search"
                label={t('groups.search')}
                placeholder={t('groups.searchGroupPlaceholder')}
                currentSearchFilter={groupSearch}
                onSearchChange={setGroupSearch}
              />
              {filteredGroups.length === 0 ? (
                <Box className={classes.noRows}>{t('groups.noRowsFound')}</Box>
              ) : (
                <RoleGroupsForm
                  role={role}
                  groups={filteredGroups}
                  control={control}
                  formState={formState}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={isFormDirty}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!isValid || !isDirty || saving || !hasGroupSelection}
          onClick={handleSubmit(saveRole)}>
          {t('common.save')}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        title={t('common.discardChanges')}
        message={t('common.possibleDataLoss')}
        open={confirmOpen}
        positiveAction={cancelDialog}
        negativeAction={handleConfirmationDialogClose}
      />
    </RootDialog>
  );
}
